import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { dispatch } from "../../../state/store";
import { setUserInsightsFilters } from "../../../state/ducks";

import { conversionTypeOptions, conversionTypes, strCode, userSegments } from "../../../consts";
import { t } from "../../../services";

import {
  FilterDropdown,
  FilterInput,
  FlexContainers,
  TextButton,
  WrapperContainer
} from "../../../components";
import FilterOptionsTable from "../../../components/FilterInput/components/FilterOptionsTable";
import { useSelectedFilterOptionsState } from "../../../components/FilterInput/hooks/useSelectedFilterOptionsState";
import { FiltersWrapper } from "../../UserManagement/UsersTable/components/FiltersTab/styled";
import { ConnectedSegmentsTableWrapper, NotConnectedSegmentsTableWrapper } from "./styled";

const UserInsightsFilters = ({ visible, setActiveFilters }) => {
  const filters = useSelector((store) => store.userInsights?.userInsightsFilters);
  const markets = useSelector((store) => store.markets?.markets) ?? [];

  const [segmentsModalActive, setSegmentsModalActive] = useState(true);

  const {
    segments: selectedSegments,
    conversionType: selectedConversionType,
    markets: selectedMarkets
  } = filters;

  const marketOptions = useMemo(() => {
    let options = markets.map((market) => {
      return {
        id: market.market,
        value: market.market
      };
    });
    return options;
  }, [markets.length]);

  const initialState = useMemo(() => {
    return {
      segments: [],
      conversionType: conversionTypes.all,
      markets: []
    };
  }, []);

  const changedState = useMemo(() => {
    return {
      segments: selectedSegments?.length,
      conversionType: selectedConversionType !== conversionTypes.all,
      markets: selectedMarkets?.length
    };
  }, [selectedSegments, selectedConversionType, selectedMarkets]);

  useEffect(() => {
    const activeFilters = Object.values(changedState).reduce(
      (acc, value) => (acc += value ? 1 : 0),
      0
    );

    setActiveFilters(activeFilters);
  }, [changedState]);

  const onSegentsChanged = (ids) => {
    const filteredSegments = userSegments.filter((el) => ids.includes(el.id));

    dispatch(
      setUserInsightsFilters({
        segments: filteredSegments ?? []
      })
    );
  };

  const onConversionTypeChanged = (id) => {
    dispatch(
      setUserInsightsFilters({
        conversionType: id
      })
    );
  };

  const onMarketsChanged = (ids) => {
    const selectedMarketsOptions = marketOptions
      .filter((el) => ids.includes(el.id))
      .map((el) => el.id);

    dispatch(
      setUserInsightsFilters({
        markets: selectedMarketsOptions ?? []
      })
    );
  };

  const clearFilters = useCallback(() => {
    dispatch(
      setUserInsightsFilters({
        segments: initialState.segments,
        conversionType: initialState.conversionType,
        markets: initialState.markets
      })
    );
  }, [initialState]);

  useEffect(() => {
    if (!selectedSegments && !selectedConversionType && !selectedMarkets) {
      clearFilters();
    }
  }, [clearFilters, selectedSegments, selectedConversionType, selectedMarkets]);

  const { selectedOptionsState, setSelectedOptions, onOptionSelected } =
    useSelectedFilterOptionsState(selectedSegments ?? [], userSegments, segmentsModalActive);

  const onSelectAll = (rows) => {
    const rowIds = rows.map((row) => row.id);
    const differentSet = selectedOptionsState?.filter((el) => !rowIds.includes(el));
    const newSelectedOptions = rowIds.filter((el) => !selectedOptionsState.includes(el));
    setSelectedOptions([...newSelectedOptions, ...differentSet]);
  };

  const SegmentFilters = (
    <WrapperContainer Container={FlexContainers.FlexColumn} gap={16}>
      <ConnectedSegmentsTableWrapper>
        <FilterOptionsTable
          onOptionsSelected={onOptionSelected}
          onSelectAll={onSelectAll}
          options={userSegments.filter((segment) => segment.connected)}
          selectedOptions={selectedOptionsState}
          columnName={strCode.CONNECTED_TITLE}
          isSearchable={false}
        />
      </ConnectedSegmentsTableWrapper>

      <NotConnectedSegmentsTableWrapper>
        <FilterOptionsTable
          onOptionsSelected={onOptionSelected}
          onSelectAll={onSelectAll}
          options={userSegments.filter((segment) => !segment.connected)}
          selectedOptions={selectedOptionsState}
          columnName={strCode.NOT_CONNECTED_TITLE}
          isSearchable={false}
        />
      </NotConnectedSegmentsTableWrapper>
    </WrapperContainer>
  );

  return (
    <FiltersWrapper
      Container={FlexContainers.FlexRowStart}
      width={"100%"}
      gap={24}
      mb={14}
      visible={visible}>
      {userSegments.length > 1 && (
        <FilterInput
          options={userSegments}
          isActive={changedState.segments}
          selectedOptions={selectedSegments}
          allOptionsText={strCode.ALL_SEGMENTS_OPTION}
          title={t(strCode.SEGMENTS_FILTER_TITLE)}
          description={t(strCode.SEGMENTS_FILTER_TEXT)}
          onSelectedOptionsChanged={onSegentsChanged}
          onModalActiveChange={setSegmentsModalActive}
          Component={SegmentFilters}
          selectedOptionsState={selectedOptionsState}
        />
      )}

      <FilterDropdown
        active={changedState.conversionType}
        options={conversionTypeOptions}
        optionsListWidth={"100%"}
        onSelectOption={onConversionTypeChanged}
        selectedOption={conversionTypeOptions.find((x) => x.id === selectedConversionType)}
        InputComponent={
          <span>
            {t(conversionTypeOptions.find((x) => x.id === selectedConversionType)?.value)}
          </span>
        }
      />

      {marketOptions.length > 1 && (
        <FilterInput
          options={marketOptions}
          isActive={changedState.markets}
          selectedOptions={marketOptions.filter((x) => selectedMarkets?.includes(x.id))}
          allOptionsText={
            marketOptions.length === 1 ? marketOptions[0].value : strCode.ALL_MARKETS_TITLE
          }
          title={t(strCode.MARKETS_SEARCH_TITLE)}
          description={t(strCode.MARKETS_SEARCH_DESCRIPTION)}
          searchPlaceholder={t(strCode.MARKETS_SEARCH_PLACEHOLDER)}
          columnName={t(strCode.MARKET_NAMES)}
          onSelectedOptionsChanged={onMarketsChanged}
        />
      )}
      {changedState.segments || changedState.conversionType || changedState.markets ? (
        <TextButton onClick={clearFilters}>{t(strCode.CLEAR_BTN)}</TextButton>
      ) : null}
    </FiltersWrapper>
  );
};

UserInsightsFilters.propTypes = {
  visible: PropTypes.bool,
  setActiveFilters: PropTypes.func.isRequired
};

export default UserInsightsFilters;
